import { Injectable } from '@angular/core';

/**
 * Minimal, dependency-free Markdown -> HTML renderer.
 * Only supports the subset of GitHub-flavoured Markdown used by the
 * DBOv2 documentation guides (headings, tables, code fences, blockquotes,
 * bold/italic/inline-code, links, bullet/numbered lists, horizontal rules).
 * Kept in-house instead of pulling in a third-party markdown package so no
 * new npm dependency / lockfile churn is introduced for a static, trusted,
 * repo-authored document.
 */
@Injectable({ providedIn: 'root' })
export class MarkdownRenderService {

  render(markdown: string): string {
    const normalized = markdown.replace(/\r\n/g, '\n');
    const lines = normalized.split('\n');
    const html: string[] = [];

    let i = 0;
    let inList: 'ul' | 'ol' | null = null;

    const closeList = () => {
      if (inList) {
        html.push(inList === 'ul' ? '</ul>' : '</ol>');
        inList = null;
      }
    };

    while (i < lines.length) {
      const line = lines[i];

      // Fenced code block
      if (/^```/.test(line.trim())) {
        closeList();
        const codeLines: string[] = [];
        i++;
        while (i < lines.length && !/^```/.test(lines[i].trim())) {
          codeLines.push(lines[i]);
          i++;
        }
        i++; // skip closing fence
        html.push(`<pre class="doc-code"><code>${this.escapeHtml(codeLines.join('\n'))}</code></pre>`);
        continue;
      }

      // Table block (header + separator row)
      if (/^\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(lines[i + 1].trim())) {
        closeList();
        const headerCells = this.splitTableRow(line);
        i += 2;
        const bodyRows: string[][] = [];
        while (i < lines.length && /^\|.*\|\s*$/.test(lines[i])) {
          bodyRows.push(this.splitTableRow(lines[i]));
          i++;
        }
        html.push('<div class="doc-table-wrap"><table class="doc-table">');
        html.push('<thead><tr>' + headerCells.map(c => `<th>${this.inline(c)}</th>`).join('') + '</tr></thead>');
        html.push('<tbody>' + bodyRows.map(r => '<tr>' + r.map(c => `<td>${this.inline(c)}</td>`).join('') + '</tr>').join('') + '</tbody>');
        html.push('</table></div>');
        continue;
      }

      // Blank line
      if (line.trim() === '') {
        closeList();
        i++;
        continue;
      }

      // Horizontal rule
      if (/^-{3,}\s*$/.test(line.trim())) {
        closeList();
        html.push('<hr class="doc-hr" />');
        i++;
        continue;
      }

      // Headings
      const headingMatch = /^(#{1,6})\s+(.*)$/.exec(line);
      if (headingMatch) {
        closeList();
        const level = headingMatch[1].length;
        html.push(`<h${level} class="doc-h doc-h${level}">${this.inline(headingMatch[2])}</h${level}>`);
        i++;
        continue;
      }

      // Blockquote (callout)
      if (/^>\s?/.test(line)) {
        closeList();
        const quoteLines: string[] = [];
        while (i < lines.length && /^>\s?/.test(lines[i])) {
          quoteLines.push(lines[i].replace(/^>\s?/, ''));
          i++;
        }
        html.push(`<blockquote class="doc-callout">${quoteLines.map(l => `<p>${this.inline(l)}</p>`).join('')}</blockquote>`);
        continue;
      }

      // Bullet list
      const bulletMatch = /^\s*[-*]\s+(.*)$/.exec(line);
      if (bulletMatch) {
        if (inList !== 'ul') {
          closeList();
          html.push('<ul class="doc-ul">');
          inList = 'ul';
        }
        html.push(`<li>${this.inline(bulletMatch[1])}</li>`);
        i++;
        continue;
      }

      // Numbered list
      const numberedMatch = /^\s*\d+\.\s+(.*)$/.exec(line);
      if (numberedMatch) {
        if (inList !== 'ol') {
          closeList();
          html.push('<ol class="doc-ol">');
          inList = 'ol';
        }
        html.push(`<li>${this.inline(numberedMatch[1])}</li>`);
        i++;
        continue;
      }

      // Paragraph
      closeList();
      html.push(`<p class="doc-p">${this.inline(line)}</p>`);
      i++;
    }

    closeList();
    return html.join('\n');
  }

  private splitTableRow(row: string): string[] {
    const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
    return trimmed.split('|').map(c => c.trim());
  }

  private inline(text: string): string {
    let escaped = this.escapeHtml(text);
    // inline code
    escaped = escaped.replace(/`([^`]+)`/g, '<code class="doc-inline-code">$1</code>');
    // bold
    escaped = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // italic
    escaped = escaped.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1<em>$2</em>');
    // links [text](url)
    escaped = escaped.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
    return escaped;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
